import React from "react";
import { useNavigate } from "react-router-dom";
import InputModal from "./InputModal";
import ConfirmModal from "./ConfirmModal";
import AddSummaryModal from "./AddSummaryModal";
import GroupReportsModal from "./GroupingReportsModal";

import {
  CREATE_REPORT_GROUP,
  RENAME_REPORT_GROUP,
  GROUP_REPORTS,
  UNGROUP_REPORTS,
  DELETE_REPORT_GROUP,
  RENAME_REPORT,
  DELETE_REPORT,
  APPLY_DRAFT,
  SAVE_AS_NEW,
  REVERT_DRAFT,
  ADD_DASHBOARD_PIN,
  REMOVE_DASHBOARD_PIN,
  UPDATE_DASHBOARD_PIN,
} from "../../../constants/reportActions";
import {
  ReportMetadataItem,
  ReportMetadataGroup,
} from "../../../models/pageModels/ReportLibrary";

import { useGroupMutations } from "../../../hooks/reports/mutations/useGroupMutations";
import { useDashboardMutations } from "../../../hooks/reports/mutations/useDashboardMutations";
import { useReportViewStore } from "../../../store/report/useReportViewStore";
import { useModal, useModalStore } from "../../../store/report/modalStore";
import { useReportMutations } from "../../../hooks/reports/mutations/useReportMutations";
import { useReportsMetadataQuery } from "../../../hooks/reports/queries/useGroupQueries";

const ReportModals: React.FC = () => {
  const navigate = useNavigate();

  const clearSelection = useReportViewStore((s) => s.clearSelection);
  const removeGroupExpanded = useReportViewStore((s) => s.removeGroupExpanded);
  const closeModal = useModalStore((s) => s.closeModal);
  
  // custom hooks
  const { data: reportsData } = useReportsMetadataQuery();
  const reports = reportsData ?? { bookmarkedReports: [], recentReports: [] };
  const { actionMutation } = useReportMutations();
  const groupMutation = useGroupMutations();
  
  const renameModal = useModal("renameReport");
  const deleteModal = useModal("deleteReport");
  const staleCheckModal = useModal("staleCheck");
  const saveAsNewModal = useModal("saveAsNew");
  const groupingModal = useModal("groupReports");
  const ungroupModal = useModal("ungroupReports");
  const renameGroupModal = useModal("renameGroup");
  const deleteGroupModal = useModal("deleteGroup");
  const addSummaryModal = useModal("addSummary");
  const renamePinModal = useModal("renamePin");
  const removePinModal = useModal("removePin");
  
  const activeGroupId = useReportViewStore((s) => s.activeGroupId);
  const effectiveGroupId = activeGroupId || addSummaryModal.data.reportGroupId || "";

  const { pinMutation } = useDashboardMutations(effectiveGroupId);


  return (
    <>
      <InputModal
        isOpen={renameModal.isOpen}
        onClose={() => closeModal("renameReport")}
        title="Rename Report"
        initialValue={renameModal.data.name}
        onConfirm={(value) =>
          actionMutation.mutateAsync({ id: renameModal.data.id, action: RENAME_REPORT, name: value }).then(() =>
            closeModal("renameReport"),
          )
        }
        confirmText="Rename"
      />

      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => closeModal("deleteReport")}
        title={
          deleteModal.data.action === REVERT_DRAFT
            ? "Discard Draft"
            : "Delete Report"
        }
        message={
          deleteModal.data.action === REVERT_DRAFT
            ? "Discard changes?"
            : "Delete this report?"
        }
        onConfirm={async () => {
          await actionMutation.mutateAsync({
            id: deleteModal.data.id,
            action: deleteModal.data.action === REVERT_DRAFT ? REVERT_DRAFT : DELETE_REPORT,
          });
          if (
            deleteModal.data.action === REVERT_DRAFT &&
            deleteModal.data.parentReportId
          ) {
            navigate(`/reports/${deleteModal.data.parentReportId}`);
          } else {
            navigate("/reports/new");
          }
          closeModal("deleteReport");
        }}
        confirmText={
          deleteModal.data.action === REVERT_DRAFT ? "Discard" : "Delete"
        }
      />

      <ConfirmModal
        isOpen={staleCheckModal.isOpen}
        onClose={() => closeModal("staleCheck")}
        title="Apply Draft"
        message="Original report was updated. Apply draft anyway?"
        onConfirm={async () => {
          const idToApply = staleCheckModal.data.reportId;
          if (idToApply) {
            const result = await actionMutation.mutateAsync({ id: idToApply, action: APPLY_DRAFT });
            if (result?.id) navigate(`/reports/${result.id}`, { replace: true });
          }
          closeModal("staleCheck");
        }}
        confirmText="Apply"
      />

      <InputModal
        isOpen={saveAsNewModal.isOpen}
        onClose={() => closeModal("saveAsNew")}
        title="Save as New Report"
        initialValue={saveAsNewModal.data.name}
        onConfirm={async (value) => {
          if (saveAsNewModal.data.id) {
            await actionMutation.mutateAsync({
              id: saveAsNewModal.data.id,
              action: SAVE_AS_NEW,
              name: value,
            });
          }
          closeModal("saveAsNew");
        }}
        confirmText="Save"
      />

      <GroupReportsModal
        isOpen={groupingModal.isOpen}
        onClose={() => closeModal("groupReports")}
        availableGroups={reports.bookmarkedReports.filter(
          (item): item is ReportMetadataGroup => item.type === "group",
        )}
        selectedItems={(groupingModal.data.reportIds ?? []).map((id) => ({
          id,
          name:
            [
              ...reports.recentReports,
              ...reports.bookmarkedReports.flatMap((item) =>
                item.type === "group"
                  ? item.reports
                  : [item as ReportMetadataItem],
              ),
            ].find((r) => r.id === id)?.name ?? "Unknown",
        }))}
        onConfirm={async (mode, data) => {
          if (mode === "new" && data.groupName) {
            await groupMutation.mutateAsync({
              action: CREATE_REPORT_GROUP,
              groupName: data.groupName,
              reportIds: groupingModal.data.reportIds,
            });
          } else if (mode === "existing" && data.groupId) {
            await groupMutation.mutateAsync({
              action: GROUP_REPORTS,
              groupId: data.groupId,
              reportIds: groupingModal.data.reportIds,
            });
          }
          clearSelection();
          closeModal("groupReports");
        }}
      />

      <InputModal
        isOpen={renameGroupModal.isOpen}
        onClose={() => closeModal("renameGroup")}
        title="Rename Group"
        initialValue={renameGroupModal.data.name}
        onConfirm={(value) =>
          groupMutation
            .mutateAsync({ action: RENAME_REPORT_GROUP, groupId: renameGroupModal.data.id, name: value })
            .then(() => closeModal("renameGroup"))
        }
        confirmText="Rename"
      />

      <ConfirmModal
        isOpen={ungroupModal.isOpen}
        onClose={() => closeModal("ungroupReports")}
        title="Ungroup Reports"
        message="Remove these reports from their groups?"
        onConfirm={async () => {
          await groupMutation.mutateAsync({ action: UNGROUP_REPORTS, reportIds: ungroupModal.data.reportIds });
          clearSelection();
          closeModal("ungroupReports");
        }}
        confirmText="Ungroup"
      />

      <ConfirmModal
        isOpen={deleteGroupModal.isOpen}
        onClose={() => closeModal("deleteGroup")}
        title="Delete Group"
        message="Delete this group and all its reports? This cannot be undone."
        onConfirm={() =>
          groupMutation
            .mutateAsync({ action: DELETE_REPORT_GROUP, groupId: deleteGroupModal.data.id })
            .then(() => {
              removeGroupExpanded(deleteGroupModal.data.id);
              closeModal("deleteGroup");
            })
        }
        confirmClassName="button warning"
        confirmText="Delete Everything"
      />

      <AddSummaryModal
        visible={addSummaryModal.isOpen}
        onClose={() => closeModal("addSummary")}
        groupId={effectiveGroupId}
        onAdd={(reportId: string, fieldName: string, displayName: string) =>
          pinMutation
            .mutateAsync({
              action: ADD_DASHBOARD_PIN,
              reportId,
              fieldName,
              displayName,
            })
            .then(() => closeModal("addSummary"))
        }
        preselectedReportId={addSummaryModal.data.preselectedReportId}
      />

      <InputModal
        isOpen={renamePinModal.isOpen}
        onClose={() => closeModal("renamePin")}
        title="Rename Pin"
        initialValue={renamePinModal.data.displayName}
        onConfirm={async (value) => {
          await pinMutation.mutateAsync({
            action: UPDATE_DASHBOARD_PIN,
            pinId: renamePinModal.data.pinId,
            displayName: value,
          });
          closeModal("renamePin");
        }}
        required
      />

      <ConfirmModal
        isOpen={removePinModal.isOpen}
        onClose={() => closeModal("removePin")}
        title="Remove Pin"
        message="Remove this summary from the dashboard?"
        onConfirm={async () => {
          await pinMutation.mutateAsync({
            action: REMOVE_DASHBOARD_PIN,
            pinId: removePinModal.data.pinId,
          });
          closeModal("removePin");
        }}
        confirmText="Remove"
        confirmClassName="button danger"
      />
    </>
  );
};

export default ReportModals;
